import type { AuthAnalyticsMethodValue } from '~/constants/auth';
import type { SiteRoleType } from '~/graphql/generated/graphql';

import { useAnalyticsService } from '~/composables/core';
import { EVENTS } from '~/constants/events';
import { UNIFIED_ROUTE_AUTH_SCHOOL_ACCOUNT } from '~/constants/routes/unified';

/** The query param that carries the refused role to the school account page, for its click events. */
export const SCHOOL_ACCOUNT_ROLE_QUERY_PARAM = 'role';

export interface SchoolAccountRefusal {
  roleType: SiteRoleType;
  /**
   * How the email was proven: `email_otp` for a login code, or `social` from the OAuth callback,
   * whose refused exchange returns no payload, so the provider is not known there (the completed
   * sign-in events spell it `social_google`, `social_apple`, …).
   */
  method: AuthAnalyticsMethodValue;
  entrySource?: string | null;
  entryCta?: string | null;
}

/**
 * A sign-up refused because the email belongs to a school account has nothing to retry where it
 * was refused: the page it lands on sends the person to the schools site or back for another email.
 */
export const useLeaveForSchoolAccount = () => {
  const analytics = useAnalyticsService();

  return async ({ roleType, method, entrySource, entryCta }: SchoolAccountRefusal) => {
    const role = roleType.toLowerCase();
    analytics.track(EVENTS.UNIFIED_AUTH_SCHOOL_ACCOUNT_REFUSED, {
      role,
      method,
      ...(entrySource ? { entrySource } : {}),
      ...(entryCta ? { entryCta } : {}),
    });
    await navigateTo({ path: UNIFIED_ROUTE_AUTH_SCHOOL_ACCOUNT, query: { [SCHOOL_ACCOUNT_ROLE_QUERY_PARAM]: role } }, { replace: true });
  };
};
